import React from 'react';
import {
  Collapse,
  List,
  Avatar,
  Button,
  Space,
  Tag,
  Badge,
  Typography,
  Empty,
} from 'antd';
import {
  UserOutlined,
  EyeOutlined,
  EditOutlined,
  UserAddOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Lead, LeadStatus } from '../../../services/lead';

const { Panel } = Collapse;
const { Text } = Typography;

interface LeadGroupViewProps {
  leads: Lead[];
  groupBy: 'status' | 'source' | 'type' | 'assignee';
  onView: (leadId: number) => void;
  onEdit?: (lead: Lead) => void;
  onClaim?: (lead: Lead) => void;
  showActions: 'public-pool' | 'my-leads';
}

const LeadGroupView: React.FC<LeadGroupViewProps> = ({
  leads,
  groupBy,
  onView,
  onEdit,
  onClaim,
  showActions,
}) => {
  // Get status color
  const getStatusColor = (status: LeadStatus) => {
    const colorMap: Record<LeadStatus, string> = {
      [LeadStatus.PUBLIC_POOL]: 'blue',
      [LeadStatus.ASSIGNED]: 'orange',
      [LeadStatus.CONVERTED]: 'green',
      [LeadStatus.RECYCLED]: 'red',
    };
    return colorMap[status] || 'default';
  };

  // Get status text
  const getStatusText = (status: LeadStatus) => {
    const textMap: Record<LeadStatus, string> = {
      [LeadStatus.PUBLIC_POOL]: '公海池',
      [LeadStatus.ASSIGNED]: '已分配',
      [LeadStatus.CONVERTED]: '已转化',
      [LeadStatus.RECYCLED]: '已回收',
    };
    return textMap[status] || status;
  };

  // Get follow status color
  const getFollowStatusColor = (followStatus: string) => {
    const colorMap: Record<string, string> = {
      '未联系': '#d9d9d9',
      '未接通': '#faad14',
      '无效': '#ff4d4f',
      '空号': '#ff4d4f',
      '稍后联系': '#1890ff',
      '重复': '#ff7a45',
      '客诉': '#f5222d',
      '已加微信': '#52c41a',
      '已加企微': '#13c2c2',
      '邀约到所': '#722ed1',
      '已成交': '#389e0d',
    };
    return colorMap[followStatus] || 'default';
  };

  // Group leads by different criteria
  const getGroupedLeads = () => {
    const groups: { [key: string]: Lead[] } = {};

    leads.forEach((lead) => {
      let groupKey = '';
      switch (groupBy) {
        case 'status':
          groupKey = getStatusText(lead.status);
          break;
        case 'source':
          groupKey = lead.data_source || '未分类';
          break;
        case 'type':
          groupKey = lead.lead_type || '未分类';
          break;
        case 'assignee':
          groupKey = lead.assignee_name || '未分配';
          break;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(lead);
    });

    return groups;
  };

  const groupedLeads = getGroupedLeads();
  const groupKeys = Object.keys(groupedLeads).sort();

  if (groupKeys.length === 0) {
    return <Empty description="暂无数据" />;
  }

  return (
    <Collapse defaultActiveKey={groupKeys}>
      {groupKeys.map((groupKey) => (
        <Panel
          key={groupKey}
          header={
            <Space>
              <Text strong>{groupKey}</Text>
              <Badge count={groupedLeads[groupKey].length} showZero />
            </Space>
          }
        >
          <List
            itemLayout="horizontal"
            dataSource={groupedLeads[groupKey]}
            renderItem={(lead) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => onView(lead.id)}
                  >
                    查看
                  </Button>,
                  showActions === 'public-pool' && onClaim ? (
                    <Button
                      type="link"
                      icon={<UserAddOutlined />}
                      onClick={() => onClaim(lead)}
                    >
                      认领
                    </Button>
                  ) : showActions === 'my-leads' && onEdit ? (
                    <Button
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => onEdit(lead)}
                    >
                      编辑
                    </Button>
                  ) : null,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<UserOutlined />}
                      style={{ backgroundColor: '#1890ff' }}
                    />
                  }
                  title={
                    <Space>
                      <Button
                        type="link"
                        onClick={() => onView(lead.id)}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        {lead.contact_name}
                      </Button>
                      {lead.follow_status && (
                        <Tag color={getFollowStatusColor(lead.follow_status)}>
                          {lead.follow_status}
                        </Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Space size="small" wrap>
                        {lead.phone && (
                          <Text type="secondary">
                            <PhoneOutlined /> {lead.phone}
                          </Text>
                        )}
                        {lead.data_source && (
                          <Text type="secondary">来源: {lead.data_source}</Text>
                        )}
                        {lead.lead_type && (
                          <Text type="secondary">类型: {lead.lead_type}</Text>
                        )}
                      </Space>
                      {lead.case_description && (
                        <Text type="secondary" ellipsis style={{ maxWidth: 500 }}>
                          {lead.case_description}
                        </Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        创建于 {dayjs(lead.created_at).format('YYYY-MM-DD HH:mm')}
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Panel>
      ))}
    </Collapse>
  );
};

export default LeadGroupView;
